import { PremiumServiceEventTypes } from "@/enums/PremiumServiceEventTypes";
import BusinessHours from 'vue-business-hours';
import GalleryPhotosUploadDlg from "@/views/staff/gallery/GalleryPhotosUploadDlg/GalleryPhotosUploadDlg.vue";
import TimeRangeDropdown from "@/components/pickers/TimeRangeDropdown.vue";
import { mapState } from "vuex";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import moment from "moment";
import { ServiceTypeEnum } from "@/enums/ServiceTypeEnum";
import { ServiceStatus } from "@/enums/ServiceStatus";
import firebase from "firebase";
import { ServiceRequest } from "@/models/ServiceRequest";
import { toTranslationKey } from "@/utils/stringFormat";
import ConfirmDlg from '@/components/popup/confirm-dialog/ConfirmDlg.vue';
import {PremiumServiceParticipantsLimit} from "@/enums/PremiumServiceParticipantsLimit";
import { ResidentsRequestTypeEnum } from "@/enums/ResidentsRequestTypeEnum";
import { CommentSenderRoleEnum } from "@/enums/CommentSenderRoleEnum";

export default {
  name: "BookEventDlg",
  components: {
    ValidationObserver,
    ValidationProvider,
    BusinessHours,
    GalleryPhotosUploadDlg,
    TimeRangeDropdown,
    ConfirmDlg
  },
  props: {
    value: Boolean
  },
  data() {
    return {
      serviceRequestForm: { serviceId: null, residentId: null, serviceType: null, comment: null },
      selectedEventService: null,
      selectedResident: null,
      bookedByResidentCount: 0,
      serviceTypeEnum: ServiceTypeEnum,
      currentDate: moment(new Date()).toDate(),
      isAlreadyBooked: false,
    }
  },
  computed: {
    ...mapState({
      facility: (state: any) => state.facilityModule.selectedFacility,
      serviceRequests: (state: any) => state.additionalServicesModule.serviceRequests,
      residents: (state: any) => state.residentModule.residentsShortData,
    }),
    open: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    facilityCurrency() {
      return this.$store.getters.facilityCurrency;
    },
    additionalEventServices() {
      const events = this.$store.state.additionalServicesModule.additionalServices.filter(x => [
        PremiumServiceEventTypes.Event,
        PremiumServiceEventTypes.EventBased,
        PremiumServiceEventTypes.ConciergeEvent,
      ].includes(x.type)).filter(x => moment(x?.start?.seconds * 1000).toDate() >= this.currentDate || !x?.hasOwnProperty('start'));

      return events
    },
    typeOfSubscription() {
      const serviceId = this.selectedEventService.serviceId;

      if (!serviceId) {
        return [...Object.values(ServiceTypeEnum)].map(x => ({ text: toTranslationKey(x), value: x }))
      }

      const { serviceType } = this.additionalEventServices.find(
        service => service.id === serviceId
      );

      if (serviceType !== ServiceTypeEnum.OneOffChargeAndDaily) {
        return [serviceType].map(x => ({ text: toTranslationKey(x), value: x }))
      } else {
        return [ServiceTypeEnum.DailyCharge, ServiceTypeEnum.OneOfCharge].map(x => ({ text: toTranslationKey(x), value: x }))
      }
    },
    premiumServiceParticipantsLimit() {
      return PremiumServiceParticipantsLimit;
    }
  },

  methods: {
    async submit() {
      try {

        if(this.isAlreadyBooked) return;

        let allowToCreate = false
       
        if (this.bookedByResidentCount >= this.selectedEventService?.participantLimitNumber && this.selectedEventService.participantLimit !== this.premiumServiceParticipantsLimit.Unlimited) {
          allowToCreate = await this.showLititPopUp()
  
          if (!allowToCreate) {
            return;
          } else {
            await this.addResidentToWaitingList();
            return;
          }
        }
  
        const isFormValid = await this.$refs.observer.validate();
  
        if (!isFormValid) {
          return;
        }
  
        this.loading = true;
        const service = this.additionalEventServices.find(service => service.id === this.serviceRequestForm.serviceId)
        if (service?.oneOffCost === ServiceTypeEnum.Free || !service?.oneOffCost?.length) {
          this.serviceRequestForm.serviceType = ServiceTypeEnum.Free;
        } else {
          this.serviceRequestForm.serviceType = service.oneOffCost === "POA" ? "POA" : ServiceTypeEnum.OneOfCharge;
        }

        let paymentCost = null;

        if(this.serviceRequestForm.serviceType) {
          if (this.serviceRequestForm.serviceType === ServiceTypeEnum.OneOfCharge) {
            paymentCost = service.oneOffCost;
          } else if (this.serviceRequestForm.serviceType === ServiceTypeEnum.DailyCharge || this.serviceRequestForm.serviceType === "Subscription") {
            paymentCost = service.subscriptionCost
          } else if (this.serviceRequestForm.serviceType === ServiceTypeEnum.Free) {
            paymentCost = null;
          } else if (this.serviceRequestForm.serviceType === "POA") {
            paymentCost = "POA"
          }
        } else {
          paymentCost = service.oneOffCost;
        }

        let status
        if (this.selectedResident.familyHubUsers.find(family => family?.financialEPOA?.find(resident => resident.id === this.serviceRequestForm.residentId && resident.status)) && this.serviceRequestForm.serviceType !== ServiceTypeEnum.Free) {
          status = ServiceStatus.AwaitingApproval
        }
        else {
          status = ServiceStatus.Approved
        }

        if(service?.oneOffCost && service?.oneOffCost === ServiceTypeEnum.Free) {
          status = ServiceStatus.Approved
        }

        const payload: ServiceRequest = {
          bookByFamilyId: null,
          bookByFamilyName: null,
          bookByResidentId: null,
          bookByResidentName: null,
          startTime: service?.start || null,
          endTime: service?.end || null,
          isStaffBooked: true,
          imageUrl: service.imageUrl,
          thumbnailUrl: service.thumbnailUrl,
          serviceId: this.serviceRequestForm.serviceId,
          residentId: this.serviceRequestForm.residentId,
          serviceType: this.serviceRequestForm.serviceType,
          bookByStaffEmail: this.$store.state.authModule.user.email,
          bookByStaffName: this.$store.state.authModule.user.name,
          residentName: this.selectedResident.name,
          serviceItemType: service.type,
          serviceName: service.name,
          providerName: service.providerName,
          category: service.category,
          paymentCost,
          facilityId: service.facilityId,
          status,
          done: false,
          paymentStatus: "ChargeToRoom",
          createdDateTime: firebase.firestore.Timestamp.fromDate(new Date()),
        }

        if (service.period) {
          payload.period = service.period
        }

        if (this.serviceRequestForm.serviceType === ServiceTypeEnum.DailyCharge) {
          payload.isSubscribe = true
        } else {
          payload.isSubscribe = false
        }

        const id = await this.$store.dispatch("createAdditionServiceRequest", payload)

        if(id){
          payload.id = id
          await this.sendComment(payload)
        }
      } 
      catch (error) {
        this.$toast.open({ message: this.$t("GeneralTranslations.UnhandledError"), type: 'error' })
      }
      finally {
        this.loading = false;
      }

      this.close();
    },
    async sendComment(serviceRequest) {

      if(!this.serviceRequestForm.comment) return

      const comment = {
        residentId: serviceRequest.residentId,
        serviceName: serviceRequest.serviceName,
        serviceItemType: serviceRequest.serviceItemType,
        senderId: this.$store.state.authModule.user.id,
        senderPhotoUrl: this.$store.state.authModule.user.profilePicUrl,
        senderName: this.$store.state.authModule.user.name,
        senderRole: CommentSenderRoleEnum.Staff,
        receiverId: serviceRequest.residentId,
        serviceRequestId: serviceRequest.id,
        read: false,
        readDateTime: null,
        text: this.serviceRequestForm.comment,
        facilityId: serviceRequest.facilityId,
      }

      await this.$store.dispatch("addComment", comment);
    },
    async onSelectResident() {
      this.countResidentBookings()
      this.selectedResident = await this.$store.dispatch("getResident", this.serviceRequestForm.residentId);
    },
    onSelectEvent() {
      this.selectedEventService = this.additionalEventServices.find(x => x.id === this.serviceRequestForm.serviceId)
      this.countResidentBookings()
      if (this.selectedEventService?.start) {
        const startDateTime = moment(this.selectedEventService.start.seconds * 1000)
        const endDateTime = moment(this.selectedEventService.end.seconds * 1000)
        this.selectedEventService.startDate = moment(startDateTime).format(this.$store.state.facilityModule.selectedFacility.dateFormat)
        this.selectedEventService.startTime = moment(startDateTime).format(this.$store.state.facilityModule.selectedFacility.timeFormat)
        this.selectedEventService.endTime = moment(endDateTime).format(this.$store.state.facilityModule.selectedFacility.timeFormat)
      }
      if(this.selectedEventService?.subscriptionCost === "POA" || this.selectedEventService?.oneOffCost === "POA"){
        this.selectedEventService.costDisplay = "POA"
      }
      else{
        const cost = parseInt(this.selectedEventService?.subscriptionCost) || parseInt(this.selectedEventService?.oneOffCost);
        this.selectedEventService.costDisplay = cost ? `${this.facilityCurrency} ${cost}` : null;
      }
    },
    countResidentBookings() {
      if (!this.selectedEventService || !this.serviceRequestForm.residentId) return

      this.isAlreadyBooked = this.serviceRequests.some(x => (
        x.serviceId === this.selectedEventService?.id && x.residentId === this.serviceRequestForm.residentId
        && ![ServiceStatus.Rejected, ServiceStatus.Cancelled].includes(x.status)))
      this.bookedByResidentCount = this.serviceRequests.filter(x => (x.serviceId === this.selectedEventService.id && ![ServiceStatus.Rejected, ServiceStatus.Cancelled].includes(x.status))).length
    },
    onTypeOfSubscriptionChange(){
      if(this.serviceRequestForm.serviceType === ServiceTypeEnum.Free){
        this.selectedEventService.costDisplay = null
      } 
    },
    clearForm() {
      this.isAlreadyBooked = false
      this.serviceRequestForm = { serviceId: null, residentId: null, serviceType: null, comment: null }
      this.selectedEventService = null
      this.selectedResident = null
      this.bookedByResidentCount = 0
      this.$refs.observer.reset()
    },
    async showLititPopUp() {
      return await (this.$refs.limitConfirmationModal as any)
        .open(null, this.$t('BookingsAndOrders.EventParticipantLimitWarning'), false)
        .then(response => {
          return (response && response.agree)
        });
    },
    close() {
      this.open = false
      this.clearForm()
    },
    async addResidentToWaitingList() {
      await firebase
        .firestore()
        .collection("additionalServices")
        .doc(this.serviceRequestForm.serviceId)
        .update({
          waitingResidentsIds: firebase.firestore.FieldValue.arrayUnion(this.serviceRequestForm.residentId),
          waitingResidents: firebase.firestore.FieldValue.arrayUnion({id: this.serviceRequestForm.residentId, name: this.selectedResident.name, bookByStaffId: this.$store.state.authModule.user.id, requestedDateTime: firebase.firestore.Timestamp.fromDate(new Date())}),
          residentsRequests: firebase.firestore.FieldValue.arrayUnion({
            residentId: this.serviceRequestForm.residentId,
            residentName: this.selectedResident.name,
            type: ResidentsRequestTypeEnum.WaitingList,
          })
        }).then(() => {
          this.$toast.open({
            type: "success",
            message: this.$t("BookingsAndOrders.SuccessAddedToWaitingList")
          });

          this.close();
      })
    }
  }
}